import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const { user, token } = useAuth();
    const [cart, setCart] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(false);
    const sessionIdRef = useRef(localStorage.getItem('cart_session_id') || `guest_${Math.random().toString(36).substring(2, 15)}`);
    const [sessionId, setSessionId] = useState(sessionIdRef.current);
    const hasFetchedRef = useRef(false);

    // Save session ID on init
    useEffect(() => {
        if (!localStorage.getItem('cart_session_id')) {
            localStorage.setItem('cart_session_id', sessionIdRef.current);
        }
    }, []);

    const getHeaders = useCallback(() => {
        const headers = { 'Content-Type': 'application/json' };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        return headers;
    }, [token]);

    const fetchCart = useCallback(async (forceRefresh = false) => {
        if (loading && !forceRefresh) return;
        setLoading(true);
        try {
            const currentSessionId = sessionIdRef.current;
            const response = await axios.get(`${API}/cart`, {
                params: { session_id: currentSessionId },
                headers: getHeaders()
            });
            setCart(response.data);
            // Save session_id for guest users
            if (response.data.session_id && !user) {
                sessionIdRef.current = response.data.session_id;
                setSessionId(response.data.session_id);
                localStorage.setItem('cart_session_id', response.data.session_id);
            }
        } catch (error) {
            console.error('Error fetching cart:', error);
            setCart({ items: [], total: 0 });
        } finally {
            setLoading(false);
        }
    }, [user, getHeaders, loading]);

    // Initial fetch on mount
    useEffect(() => {
        if (!hasFetchedRef.current) {
            hasFetchedRef.current = true;
            fetchCart(true);
        }
    }, []);

    // Merge guest cart when user logs in
    useEffect(() => {
        const mergeCart = async () => {
            const guestSessionId = sessionIdRef.current;
            if (user && token && guestSessionId && guestSessionId.startsWith('guest_')) {
                try {
                    await axios.post(`${API}/cart/merge`, null, {
                        params: { session_id: guestSessionId },
                        headers: getHeaders()
                    });
                    // Generate new session for next guest session
                    const newSessionId = `guest_${Math.random().toString(36).substring(2, 15)}`;
                    sessionIdRef.current = newSessionId;
                    setSessionId(newSessionId);
                    localStorage.setItem('cart_session_id', newSessionId);
                    fetchCart(true);
                } catch (error) {
                    console.error('Error merging cart:', error);
                }
            }
        };
        mergeCart();
    }, [user, token, getHeaders]);

    const addToCart = async (productId, quantity = 1, selectedVariations = {}) => {
        try {
            const currentSessionId = sessionIdRef.current;
            const response = await axios.post(`${API}/cart/add`, 
                { product_id: productId, quantity, selected_variations: selectedVariations },
                { 
                    params: { session_id: currentSessionId },
                    headers: getHeaders()
                }
            );
            // Update session ID if returned
            if (response.data.session_id && !user) {
                sessionIdRef.current = response.data.session_id;
                setSessionId(response.data.session_id);
                localStorage.setItem('cart_session_id', response.data.session_id);
            }
            await fetchCart(true);
            return true;
        } catch (error) {
            console.error('Error adding to cart:', error);
            throw error;
        }
    };

    const updateQuantity = async (productId, quantity) => {
        try {
            await axios.put(`${API}/cart/update`,
                { product_id: productId, quantity, selected_variations: {} },
                {
                    params: { session_id: sessionIdRef.current },
                    headers: getHeaders()
                }
            );
            await fetchCart(true);
        } catch (error) {
            console.error('Error updating cart:', error);
        }
    };

    const removeFromCart = async (productId) => {
        try {
            await axios.delete(`${API}/cart/remove/${productId}`, {
                params: { session_id: sessionIdRef.current },
                headers: getHeaders()
            });
            await fetchCart(true);
        } catch (error) {
            console.error('Error removing from cart:', error);
        }
    };

    const clearCart = useCallback(() => {
        setCart({ items: [], total: 0 });
    }, []);

    const itemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

    const contextValue = useMemo(() => ({
        cart,
        loading,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        fetchCart,
        itemCount,
        sessionId
    }), [cart, loading, addToCart, updateQuantity, removeFromCart, clearCart, fetchCart, itemCount, sessionId]);

    return (
        <CartContext.Provider value={contextValue}>
            {children}
        </CartContext.Provider>
    );
};
